import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../pages/login/AuthContext";
import {
  Tooltip,
  Avatar,
} from "@mui/material";
import {
  FaBars,
  FaXmark,
  FaThumbtack,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa6";
import "./sidebar.css";
import UserMenu from "./UserMenu";
import axios from "axios";
import config from "../config/config";
import { menuItems } from "./menuItems";
import { toast } from "react-toastify";

const logoPath = "/logo512.png";

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, logout, isAuthenticated, hasPermission, token } = useAuth();
  const [active, setActive] = useState("Home");
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const saved = localStorage.getItem("sidebarExpandedGroups");
    return saved ? JSON.parse(saved) : { GastoraLicense: true };
  });

  // LocalStorage'dan sidebar tercihini oku
  useEffect(() => {
    const pinned = localStorage.getItem("sidebarPinned");
    if (pinned !== null) setIsPinned(pinned === "true");
  }, []);


const handleCopy = async () => {
  try {
    if (!userData?.id) {
      toast.warning("Kullanıcı bilgisi bulunamadı!");
      return;
    }

    const axiosConfig = {
      headers: { Authorization: `Bearer ${token}` },
    };

    // Token'i kopyalamadan önce backend'den çek
    const res = await axios.get(
      `${config.baseURL}/kullanici/kullanici-token/${userData.id}`,
      axiosConfig
    );

    const currentToken = res.data?.success ? res.data.data?.OTPToken : null;

    if (!currentToken) {
      toast.warning("Token bulunamadı!");
      return;
    }

    setOtpToken(currentToken);
    await navigator.clipboard.writeText(currentToken);
    setCopied(true);
    toast.success(`Token başarıyla kopyalandı! (${currentToken})`);

    setTimeout(() => setCopied(false), 1500);
  } catch (err) {
    console.error("Kopyalama hatası:", err);
    toast.error("Token kopyalanırken bir hata oluştu!");
  }
};


  const handleLogOut = async () => {
    try {
      setIsLoading(true);
      await logout();
    } catch (error) {
      console.error("Çıkış yapılırken hata oluştu:", error);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    const path = location.pathname;
    const matches = (itemPath) => {
      if (!itemPath) return false;
      if (itemPath === "/") return path === "/";
      return path === itemPath || path.startsWith(`${itemPath}/`);
    };

    let best = null;
    let bestLen = -1;
    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (matches(child.path) && child.path.length > bestLen) {
            best = { activeId: child.id, groupId: item.id };
            bestLen = child.path.length;
          }
        }
      } else if (matches(item.path) && item.path.length > bestLen) {
        best = { activeId: item.id, groupId: null };
        bestLen = item.path.length;
      }
    }
    if (!best) return;
    setActive(best.activeId);
    if (best.groupId) {
      setExpandedGroups((prev) => ({ ...prev, [best.groupId]: true }));
    }
  }, [location]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      localStorage.setItem("sidebarExpandedGroups", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  if (!isAuthenticated) return null;

  const isExpanded = isPinned || isHovered;

  const visibleMenuItems = menuItems.filter(
    (item) => !item.adminOnly || hasPermission("admin"),
  );

  const renderMenuItem = (item) => {
    if (item.children) {
      const isGroupOpen = expandedGroups[item.id];
      const childActive = item.children.some((c) => active === c.id);
      return (
        <li key={item.id} className="menu-group">
          <Tooltip title={!isExpanded ? item.text : ""} placement="right" arrow>
            <div
              className={`menu-item menu-group-header ${isGroupOpen ? "group-open" : ""} ${
                childActive && !isExpanded ? "active-menu" : ""
              }`}
              onClick={() => {
                if (!isExpanded) return;
                toggleGroup(item.id);
              }}
            >
              <item.icon className="menu-icons" />
              <span className="menu-text">{item.text}</span>
              {isExpanded && (
                <span className="menu-group-chevron">
                  {isGroupOpen ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                </span>
              )}
            </div>
          </Tooltip>
          {isGroupOpen && isExpanded && (
            <ul className="menu-sublist">
              {item.children.map((child) => (
                <Tooltip key={child.id} title={!isExpanded ? child.text : ""} placement="right" arrow>
                  <li
                    className={`menu-item menu-subitem ${active === child.id ? "active-menu" : ""}`}
                    onClick={() => {
                      navigate(child.path);
                      setActive(child.id);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {child.icon && <child.icon className="menu-icons sub-icon" />}
                    <span className="menu-text">{child.text}</span>
                  </li>
                </Tooltip>
              ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <Tooltip
        key={item.id}
        title={!isExpanded ? item.text : ""}
        placement="right"
        arrow
      >
        <li
          className={`menu-item ${active === item.id ? "active-menu" : ""}`}
          onClick={() => {
            navigate(item.path);
            setActive(item.id);
            setIsMobileMenuOpen(false);
          }}
        >
          <item.icon className="menu-icons" />
          <span className="menu-text">{item.text}</span>
        </li>
      </Tooltip>
    );
  };

  const togglePin = () => {
    const newPinned = !isPinned;
    setIsPinned(newPinned);
    localStorage.setItem("sidebarPinned", newPinned.toString());
  };
  return (
    <>
      {/* Mobil menü butonu */}
      <button
        className="hamburger-btn"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? <FaXmark size={24} /> : <FaBars size={24} />}
      </button>

      <div
        className={`overlay ${isMobileMenuOpen ? "active" : ""}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      <div
        className={`side-bar ${isMobileMenuOpen ? "active" : ""} ${
          isExpanded ? "expanded" : "collapsed"
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Sabitleme */}
        <Tooltip placement="right">
          <button
            className={`pin-toggle ${isPinned ? "pinned" : ""}`}
            onClick={togglePin}
            aria-label={isPinned ? "Sabitlemeyi Kaldır" : "Sabitle"}
          >
            <FaThumbtack size={16} />
          </button>
        </Tooltip>

        {/* Logo */}
        <div className="logo-container">
          <img src={logoPath} alt="Pozitif Ofis Logo" />
          <div className="logo-text">
            <span className="company-name">Pozitif Ofis</span>
          </div>
        </div>

        {/* Menü */}
        <div className="menu">
          <ul className="menu-list">
            {visibleMenuItems.map(renderMenuItem)}
          </ul>
        </div>

        {/* Kullanıcı kutusu */}
        <div className="user-box">
          <div className="user-info">
            <div
              title="Profili Düzenle"
              onClick={() => navigate("/profil")}
              style={{ cursor: "pointer" }}
            >
             <Avatar
  sx={{
    width: 36,
    height: 36,
    fontSize: '16px',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #CBD5E1 0%, #94A3B8 100%)',
    color: '#1C355E',
    border: '1px solid rgba(255, 255, 255, 0.25)',
  }}
>

  {/* {userData?.fullname?.split(' ').map(n => n[0]).join('').slice(0, 2) || "U"} */}
{userData?.fullname?.split(' ')[0]?.[0] || "U"}
</Avatar>
            </div>

            {isExpanded && (
              <>
                <div className="user-details">
                  <span className="username">
                    {userData?.fullname || "Misafir"}
                  </span>
                  <span className="fullname">
                    {userData?.company || "İsimsiz Kullanıcı"}
                  </span>
                </div>

                <UserMenu
                  userData={userData}
                  token={otpToken}
                  copied={copied}
                  handleCopy={handleCopy}
                  handleLogout={handleLogOut}
                  navigate={navigate}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;